import React from 'react';

import { permissionChecks } from '../../../../../../utils/permission-check';
import { hasContainerData } from '../../../../../../viewer/timeline-manager';
import { ViewSettings } from '../../../../../../viewer/view-settings';
import { useActiveTimelineContainer } from '../../../../../hooks/use-active-timeline-container';
import { OrthophotoTool } from '../orthophoto-tool';
import { PerspectiveTool } from '../perspective-tool';
import { PointCloudTool } from '../point-cloud/point-cloud-tool';
import { FlexibleCameraTool } from './flexible-camera-tool';
import { useViewSettings } from './use-view-settings';

/**
 * The views where the orientation cube isn't shown, in the toolbar or in the timeline bar's notch:
 * one per layer, or the cameras for a timeline container without data. None where the views are
 * the cameras anyway.
 */
const useClassicViewTools = (toolbarRef: React.RefObject<HTMLElement>) => {
  // Re-renders when the viewing mode changes
  useViewSettings();
  const activeTimelineContainer = useActiveTimelineContainer();

  if (ViewSettings.instance.showsCameras) {
    return [];
  }

  const hasTiledModelSceneAccess = permissionChecks.hasAccessToFeature(
    'feature:viewer:toolbar:tiled_model_scene',
  );
  const hasOrthoSceneAccess = permissionChecks.hasAccessToFeature(
    'feature:viewer:toolbar:ortho_tile_scene',
  );
  const hasPointCloudSceneAccess = permissionChecks.hasAccessToFeature(
    'feature:viewer:toolbar:point_cloud_scene',
  );
  // A timeline container without data has no classic view to show, so the cameras take their place
  const hasNoTimelineData = !!activeTimelineContainer && !hasContainerData(activeTimelineContainer);

  return (
    hasNoTimelineData
      ? [
          (hasTiledModelSceneAccess || hasPointCloudSceneAccess) && (
            <FlexibleCameraTool camera="perspective" key="flexiblePerspectiveTool" />
          ),
          hasOrthoSceneAccess && (
            <FlexibleCameraTool camera="orthographic" key="flexibleOrthographicTool" />
          ),
        ]
      : [
          hasTiledModelSceneAccess && <PerspectiveTool key="perspectiveTool" />,
          hasOrthoSceneAccess && <OrthophotoTool key="orthophotoTool" />,
          hasPointCloudSceneAccess && (
            <PointCloudTool
              key="pointCloudTool"
              toolbarRef={toolbarRef}
              withSettings={!ViewSettings.instance.isEnabled}
            />
          ),
        ]
  ).filter((x): x is React.JSX.Element => !!x);
};

export { useClassicViewTools };
